/**
 * Tempo utilities for converting between ticks and milliseconds
 */

import type { ScoreV2, TempoIndexPoint } from "./types";

/**
 * Build tempo index for fast tick<->ms conversions
 */
export function buildTempoIndex(
  tempoMap: Array<{ tick: number; bpm: number }>,
  ppq: number
): TempoIndexPoint[] {
  let tm = Array.isArray(tempoMap) ? tempoMap.slice() : [];
  tm = tm
    .map((t) => ({ tick: t.tick, bpm: t.bpm }))
    .filter((t) => t.tick >= 0 && t.bpm > 0)
    .sort((a, b) => a.tick - b.tick);

  if (tm.length === 0 || tm[0].tick !== 0) {
    tm.unshift({ tick: 0, bpm: tm[0]?.bpm ?? 120 });
  }

  const idx: TempoIndexPoint[] = [];
  let prevTick = tm[0].tick;
  let prevBpm = tm[0].bpm;
  let msAtTick = 0;

  idx.push({ tick: prevTick, bpm: prevBpm, msAtTick });

  for (let i = 1; i < tm.length; i++) {
    const cur = tm[i];
    const dtTicks = cur.tick - prevTick;
    if (dtTicks > 0) {
      msAtTick += (dtTicks * 60000) / (prevBpm * ppq);
    }
    prevTick = cur.tick;
    prevBpm = cur.bpm;
    idx.push({ tick: prevTick, bpm: prevBpm, msAtTick });
  }
  return idx;
}

/**
 * Find tempo point index for a given tick
 */
function findTempoPoint(tempoIndex: TempoIndexPoint[], tick: number): number {
  let lo = 0;
  let hi = tempoIndex.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const mt = tempoIndex[mid].tick;
    if (mt === tick) return mid;
    if (mt < tick) lo = mid + 1;
    else hi = mid - 1;
  }
  return Math.max(0, hi);
}

/**
 * Convert tick to milliseconds
 */
export function tickToMsFast(
  tick: number,
  tempoIndex: TempoIndexPoint[],
  ppq: number
): number {
  if (!tempoIndex.length) {
    return (Math.max(0, tick) * 60000) / (120 * ppq);
  }
  const t = Math.max(0, tick);
  const i = findTempoPoint(tempoIndex, t);
  const p = tempoIndex[i];
  const deltaTicks = t - p.tick;
  const deltaMs = (deltaTicks * 60000) / (p.bpm * ppq);
  return p.msAtTick + deltaMs;
}

/**
 * Convert milliseconds to tick
 */
export function msToTickFast(
  ms: number,
  tempoIndex: TempoIndexPoint[],
  ppq: number
): number {
  const targetMs = ms;
  if (!tempoIndex.length) {
    return Math.round((Math.max(0, targetMs) * 120 * ppq) / 60000);
  }

  let curBpm = tempoIndex[0].bpm;
  let prevTick = tempoIndex[0].tick;
  let accMs = 0;

  for (let i = 1; i < tempoIndex.length; i++) {
    const nextTick = tempoIndex[i].tick;
    const segTicks = Math.max(0, nextTick - prevTick);
    const segMs = (segTicks * 60000) / (curBpm * ppq);

    if (targetMs <= accMs + segMs) {
      const remMs = targetMs - accMs;
      const addTicks = (remMs * curBpm * ppq) / 60000;
      return Math.round(prevTick + addTicks);
    }

    accMs += segMs;
    prevTick = nextTick;
    curBpm = tempoIndex[i].bpm;
  }

  const remMs = Math.max(0, targetMs - accMs);
  const addTicks = (remMs * curBpm * ppq) / 60000;
  return Math.round(prevTick + addTicks);
}

/**
 * Find index where event.t0 >= tick (lower bound)
 */
export function lowerBoundByT0(
  events: Array<{ t0: number; dt: number; m: number }>,
  tick: number
): number {
  let lo = 0,
    hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid].t0 < tick) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Get the last tick of the score
 */
export function lastTickOf(score: ScoreV2): number {
  if (!score?.events?.length) return 0;
  const last = score.events[score.events.length - 1];
  return last.t0 + last.dt;
}

/**
 * Get active mask at given millisecond position
 */
export function maskAtMs(
  score: ScoreV2,
  tempoIndex: TempoIndexPoint[],
  posMs: number
): number {
  const events = score?.events ?? [];
  if (!events.length) return 0;

  const curTick = msToTickFast(posMs, tempoIndex, score.ppq);
  const idx = lowerBoundByT0(events, curTick);
  const start = Math.max(0, idx - 120);

  let mask = 0;
  for (let i = start; i < events.length; i++) {
    const e = events[i];
    if (e.t0 > curTick + 6 * score.ppq) break;

    const eventMs = tickToMsFast(e.t0, tempoIndex, score.ppq);
    const ti = findTempoPoint(tempoIndex, e.t0);
    const bpmHere = tempoIndex[ti]?.bpm ?? 120;
    const dtMs = (e.dt * 60000) / (bpmHere * score.ppq);

    if (posMs >= eventMs && posMs < eventMs + dtMs) {
      mask |= e.m;
    }
  }
  return mask;
}
